import React, { useState } from "react";
import { Stack, Box, Typography, Divider, Pagination } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Skeleton from "@mui/material/Skeleton";
import NGOCard from "~/components/NGOs/Card";
import OngsClient from "../api/OngsClient";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import NoItemsMessage from "~/components/NoItemsMessage";
import SearchField from '~/components/SearchField';
import { useScrollToSection } from '~/single-page/hooks/useScrollToSection';
import { useNormalizedSearch } from '~/single-page/hooks/useNormalizedSearch';

const LoadingSkeleton = () => (
  <Grid container spacing={2}>
    {Array.from(new Array(6)).map((_, index) => (
      <Grid xs={12} sm={12} md={6} key={index}>
        <Skeleton variant="rectangular" height={118} />
      </Grid>
    ))}
  </Grid>
);

export default function NgosPage() {
  const {
    data: ongs = [],
    success,
    pending,
  } = useApiCall({
    apiCall: OngsClient.ongs,
    init: true,
  });

  useScrollToSection();

  const [searchTerm, setSearchTerm] = useState('');
  const [normalizedSearchTerm, setNormalizedSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const filteredOngs = useNormalizedSearch(ongs, normalizedSearchTerm, [
    'name',
    'description'
  ]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(filteredOngs.length / itemsPerPage);
  const currentOngs = filteredOngs.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (pending) {
    return <LoadingSkeleton />;
  }

  if (!success || !ongs.length) {
    return (
      <Box sx={{ margin: '2rem 0' }}>
        <NoItemsMessage itemName="ONG/PROJETO" gender="feminino" />
      </Box>
    );
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: '100%' }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, mt: 2, textAlign: 'center' }}>
          ONGs e Projetos Parceiros
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4, 
            textAlign: "center",
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          Conheça as ONGs e projetos sociais que fazem parte da nossa rede de impacto. 
          São organizações comprometidas com diferentes causas, mas unidas pelo mesmo propósito: 
          transformar realidades. Explore, conecte-se e descubra como você pode fazer parte 
          dessas iniciativas que estão mudando o Recife.
        </Typography>
      </Box>

      <SearchField 
        value={searchTerm}
        onChange={(e) => { 
          setSearchTerm(e.target.value);
          setNormalizedSearchTerm(e.target.normalizedValue);
          setPage(1);
        }}
        label="Pesquisar Projetos ou ONGs..."
        placeholder="Digite nome da ONG, Projeto ou descrição"
      />

      <Divider sx={{ width: "100%" }} />

      <Box>
        <Grid 
          container 
          spacing={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(2, 1fr)'
            },
            gap: 2
          }}
        >
          {currentOngs.map((ong) => (
            <Grid 
              key={ong.id}
              sx={{ 
                width: '100%',
                height: '100%'
              }}
            >
              <NGOCard 
                ong={ong} 
                sx={{ height: '100%' }}
              />
            </Grid>
          ))}
        </Grid>

        {filteredOngs.length === 0 && searchTerm && (
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            py: 4 
          }}>
            <Typography color="text.secondary">
              Nenhuma ONG ou projeto encontrados com o termo "{searchTerm}"
            </Typography>
          </Box>
        )}

        {filteredOngs.length > itemsPerPage && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mt: 4
          }}>
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}
